import React from 'react'
import classnames from 'classnames'
import { SegmentBuilderContext } from './segment-builder-context'
import { Well, FileUploader } from '@pushly/aqe/lib/components'
import { OrgSegmentModel } from '../../models/segments/org-segment.model'
import { SegmentModel } from '../../models/segments/segment.model'
import { QuestionCircleOutlined } from '@ant-design/icons'
import SegmentOrgDetailsBuilder from './segment-org-details-builder'
import MultiDomainHeaderAlert from './multi-domain-header-alert'
import { simpleNotification } from '../../_utils/utils'
import { SegmentSource } from '@pushly/models/lib/enums'
import { Form, Input, Skeleton, Space, Switch, Tooltip } from 'antd'

interface IIconFieldProps {
    onChange: (segment: OrgSegmentModel | SegmentModel) => void
    onEnabledChange: (enabled: boolean) => void
}

const SegmentIconField = ({ onChange, onEnabledChange }: IIconFieldProps) => {
    const context = React.useContext(SegmentBuilderContext)

    const owner = context.level === 'org' ? context.org : context.domain
    const segment = context.segment

    return (
        <Space vertical className="segment-builder-default-icon-field">
            <label>
                <Switch
                    size="small"
                    checked={context.defaultIconEnabled}
                    disabled={context.readonly}
                    onChange={(enabled) => {
                        if (!enabled) {
                            const update = segment.clone()
                            update.setIconUrl(null)
                            onChange(update)
                        }

                        onEnabledChange(enabled)
                    }}
                />
                <span className="label-with-component-l">Override default icon when targeting this segment.</span>
            </label>

            {context.defaultIconEnabled && (
                <FileUploader
                    type="image"
                    buttonText="Icon"
                    disabled={context.readonly}
                    ownerType={context.level}
                    ownerId={owner.id}
                    defaultValue={
                        !segment.getIconUrl()
                            ? undefined
                            : {
                                  url: segment.getIconUrl()!,
                              }
                    }
                    imageConfig={{
                        resize: FileUploader.defaultDimensions.notificationIcon,
                        cropMax: FileUploader.defaultDimensions.notificationIcon,
                        cropRatio: 1,
                        quality: owner.mediaQuality,
                    }}
                    onSuccess={(iconUpload) => {
                        const update = segment.clone()
                        update.setIconUrl(iconUpload.url)
                        onChange(update)
                    }}
                    onRemove={() => {
                        const update = segment.clone()
                        update.setIconUrl(null)
                        onChange(update)
                    }}
                    onError={(res: any) => {
                        const error = res?.error
                        if (error && error.error_type === 'invalid_file_type_error') {
                            return simpleNotification('error', error.message)
                        }
                    }}
                />
            )}
        </Space>
    )
}

interface ISingleUseFieldProps {
    onChange: (segment: OrgSegmentModel | SegmentModel) => void
    onEnabledChange: (enabled: boolean) => void
}

const SingleUseField = ({ onChange, onEnabledChange }: ISingleUseFieldProps) => {
    const context = React.useContext(SegmentBuilderContext)

    const segment = context.segment

    return (
        <div className="switch-row segment-build-adhoc-switch">
            <Form.Item
                label={
                    <>
                        <span>Single-Use Segment</span>
                        <Tooltip
                            title={
                                <span>
                                    Enabling marks this segment as a single-use segment. It will not be shown in the
                                    segment dropdown or segment list after this use.
                                </span>
                            }
                        >
                            <QuestionCircleOutlined className="info-icon" />
                        </Tooltip>
                    </>
                }
            >
                <Switch
                    size="small"
                    checked={context.adhocEnabled}
                    onChange={(enabled) => {
                        const update = segment.clone()
                        update.setSource(enabled ? SegmentSource.ADHOC : SegmentSource.STANDARD)
                        onChange(update)
                        onEnabledChange(enabled)
                    }}
                />
            </Form.Item>
        </div>
    )
}

interface IDetailsBuilderProps {
    onChange: (segment: OrgSegmentModel | SegmentModel) => void
    onAdhocEnabledChange: (enabled: boolean) => void
    onDefaultIconEnabledChange: (enabled: boolean) => void
    onDomainIdsChange: (domainIds: number[], requiresValidation: boolean) => void
}

const SegmentDetailsBuilder = (props: IDetailsBuilderProps) => {
    const context = React.useContext(SegmentBuilderContext)
    const detailsDependenciesLoaded = context.levelDependenciesLoaded && context.segmentDependenciesLoaded

    const isMemberOfMultiDomain = context.segment instanceof SegmentModel && context.segment.getIsMultiDomain()

    return (
        <React.Fragment>
            {isMemberOfMultiDomain && <MultiDomainHeaderAlert />}

            <Well
                className={classnames('details-well', 'segment-builder-details-builder', {
                    blur: context.level === 'org' && context.confirmingDomainChange,
                })}
                title="Details"
                footerMode="hidden"
                variant={context.isDrawerMode ? 'ghost' : 'default'}
                padding={context.isDrawerMode ? 'none' : 'base'}
            >
                <Skeleton loading={!detailsDependenciesLoaded} active={true}>
                    {detailsDependenciesLoaded && context.segment && (
                        <React.Fragment>
                            <Form.Item label="Name" layout="vertical">
                                <Input
                                    size={context.mode === 'drawer' ? 'small' : 'large'}
                                    placeholder="Provide a unique name for your segment"
                                    defaultValue={context.segment.getName()}
                                    disabled={context.readonly}
                                    onChange={(ev) => {
                                        const update = context.segment.clone()
                                        update.setName(ev.target.value)

                                        props.onChange(update)
                                    }}
                                />
                            </Form.Item>

                            {/* Org Segment only fields */}
                            {context.level === 'org' && <SegmentOrgDetailsBuilder onChange={props.onDomainIdsChange} />}

                            {/* Drawer Mode only fields */}
                            {context.isDrawerMode && (
                                <SingleUseField
                                    onChange={props.onChange}
                                    onEnabledChange={props.onAdhocEnabledChange}
                                />
                            )}

                            {/* Standard Mode only fields */}
                            {!context.isDrawerMode && (
                                <SegmentIconField
                                    onChange={props.onChange}
                                    onEnabledChange={props.onDefaultIconEnabledChange}
                                />
                            )}
                        </React.Fragment>
                    )}
                </Skeleton>
            </Well>
        </React.Fragment>
    )
}

export default SegmentDetailsBuilder
